'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useRef, useState, useEffect, useMemo } from 'react';
import {
  Facebook,
  Instagram,
  Linkedin,
  Phone,
  Mail,
  MapPin,
} from 'lucide-react';
import { motion, useInView, type Transition } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useParams } from 'next/navigation';
import { Locale, isRTLLanguage } from '@/lib/i18n/config';
import { businessData } from '@/lib/business-data';

// ─── Office hours config ──────────────────────────────────────────────────────
const OPEN_HOUR = 10;
const CLOSE_HOUR_WEEKDAY = 19;
const CLOSE_HOUR_SATURDAY = 19;

/** Extract weekday (0=Sun) and hour from Europe/Istanbul using Intl API */
function getIstanbulTime(): { day: number; hour: number } {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Istanbul',
    weekday: 'short',
    hour: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(now);
  const weekdayStr = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const hourStr = parts.find((p) => p.type === 'hour')?.value ?? '0';
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return { day: dayMap[weekdayStr] ?? 0, hour: parseInt(hourStr, 10) };
}

function computeOpenStatus(labels: { closedToday: string; currentlyOpen: string; currentlyClosed: string }): { open: boolean; label: string } {
  const { day, hour } = getIstanbulTime();
  if (day === 0) {
    return { open: false, label: labels.closedToday };
  } else if (day === 6) {
    const open = hour >= OPEN_HOUR && hour < CLOSE_HOUR_SATURDAY;
    return { open, label: open ? labels.currentlyOpen : labels.currentlyClosed };
  } else {
    const open = hour >= OPEN_HOUR && hour < CLOSE_HOUR_WEEKDAY;
    return { open, label: open ? labels.currentlyOpen : labels.currentlyClosed };
  }
}

function useIsOpen(labels: { closedToday: string; currentlyOpen: string; currentlyClosed: string }): { open: boolean; label: string } {
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const [result, setResult] = useState<{ open: boolean; label: string }>(() =>
    computeOpenStatus(labels)
  );

  useEffect(() => {
    setResult(computeOpenStatus(labelsRef.current));
  }, [labels.currentlyOpen, labels.currentlyClosed, labels.closedToday]);

  useEffect(() => {
    const interval = setInterval(() => {
      setResult(computeOpenStatus(labelsRef.current));
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  return result;
}

// ─── Animation helpers ────────────────────────────────────────────────────────
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    } as Transition,
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: 'easeOut' } as Transition,
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function ColumnHeading({ children, isRtl }: { children: React.ReactNode; isRtl: boolean }) {
  return (
    <h3
      className={cn(
        'text-sm font-semibold tracking-[0.15em] uppercase text-white/50 mb-5',
        isRtl && 'font-arabic'
      )}
      style={{ fontFamily: 'var(--font-display, inherit)' }}
    >
      {children}
    </h3>
  );
}

function FooterLink({
  href,
  children,
  isRtl,
  external,
}: {
  href: string;
  children: React.ReactNode;
  isRtl?: boolean;
  external?: boolean;
}) {
  const props = external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <li>
      <Link
        href={href}
        className={cn(
          'group flex items-center gap-2 text-white/65 hover:text-white transition-colors duration-200 text-sm',
          isRtl && 'font-arabic flex-row-reverse'
        )}
        {...props}
      >
        {/* Expanding gold rule on hover */}
        <span
          className={cn(
            'block h-px bg-gold transition-all duration-300 w-0 group-hover:w-4 shrink-0',
            isRtl && 'order-last'
          )}
          aria-hidden="true"
        />
        {children}
      </Link>
    </li>
  );
}

function SocialIcon({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      className="group inline-flex items-center justify-center w-9 h-9 border border-white/20 rounded-md text-white/60 hover:text-gold hover:border-gold transition-all duration-200"
    >
      {children}
    </Link>
  );
}

function HoursRow({
  day,
  hours,
  closed,
  closedLabel,
}: {
  day: string;
  hours?: string;
  closed?: boolean;
  closedLabel: string;
}) {
  return (
    <li className="flex items-center justify-between text-sm">
      <span className="text-white/60">{day}</span>
      <span className={cn('font-medium', closed ? 'text-red-400' : 'text-white/90')}>
        {closed ? closedLabel : hours}
      </span>
    </li>
  );
}

function ContactRow({
  icon,
  children,
  isRtl,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  isRtl: boolean;
}) {
  return (
    <li className={cn('flex items-start gap-3', isRtl && 'flex-row-reverse text-right')}>
      <div className="shrink-0 mt-0.5 text-gold">{icon}</div>
      <div className={cn('text-sm text-white/70 leading-relaxed', isRtl && 'font-arabic')}>
        {children}
      </div>
    </li>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Footer() {
  const { t, locale } = useTranslation('common');
  const params = useParams();
  const currentLocale = (params?.locale as Locale) || locale;
  const isRtl = isRTLLanguage(currentLocale);
  const openLabels = useMemo(() => ({
    closedToday: t('footer.closedToday'),
    currentlyOpen: t('footer.currentlyOpen'),
    currentlyClosed: t('footer.currentlyClosed'),
  }), [t]);
  const { open: isOpen, label: openLabel } = useIsOpen(openLabels);

  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const localizedPath = (path: string) => `/${currentLocale}${path}`;

  return (
    <footer
      className="text-white"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {/* ── Band 1: Navy five-column content area ────────────────────────────── */}
      <div className="bg-[#222D52]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8"
          >
            {/* Col 1: Brand */}
            <motion.div variants={itemVariants} className="sm:col-span-2 lg:col-span-1">
              <Link href={localizedPath('/')} className="inline-block mb-4">
                <Image
                  src="/images/logos/Logo.webp"
                  alt="DentaWista"
                  width={78}
                  height={56}
                  style={{ width: "auto", height: "3.5rem" }}
                  className="brightness-0 invert"
                />
              </Link>
              <p
                className={cn(
                  'text-white/60 text-sm leading-relaxed mb-6 max-w-[220px]',
                  isRtl && 'font-arabic'
                )}
              >
                {t('footer.tagline')}
              </p>

              {/* Social icons */}
              <div
                className={cn(
                  'flex items-center gap-2',
                  isRtl ? 'flex-row-reverse' : 'flex-row'
                )}
              >
                <SocialIcon
                  href="https://www.facebook.com/people/DentaWista-Clinic/61563466250495/"
                  label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </SocialIcon>
                <SocialIcon href="https://x.com/dentawista" label="X">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </SocialIcon>
                <SocialIcon href="https://www.instagram.com/dentawista/" label="Instagram">
                  <Instagram className="w-4 h-4" />
                </SocialIcon>
                <SocialIcon
                  href="https://tr.linkedin.com/company/dentawista-clinic"
                  label="LinkedIn"
                >
                  <Linkedin className="w-4 h-4" />
                </SocialIcon>
              </div>
            </motion.div>

            {/* Col 2: Quick Links */}
            <motion.div variants={itemVariants}>
              <ColumnHeading isRtl={isRtl}>{t('footer.quickLinks')}</ColumnHeading>
              <ul className="space-y-3">
                <FooterLink href={localizedPath('/about')} isRtl={isRtl}>
                  {t('footer.links.about')}
                </FooterLink>
                <FooterLink href={localizedPath('/treatments')} isRtl={isRtl}>
                  {t('footer.links.treatments')}
                </FooterLink>
                <FooterLink href={localizedPath('/testimonials')} isRtl={isRtl}>
                  {t('footer.links.testimonials')}
                </FooterLink>
                <FooterLink href={localizedPath('/contact')} isRtl={isRtl}>
                  {t('footer.links.contact')}
                </FooterLink>
                <FooterLink href={localizedPath('/book-appointment')} isRtl={isRtl}>
                  {t('common.bookNow')}
                </FooterLink>
              </ul>
            </motion.div>

            {/* Col 3: Treatments */}
            <motion.div variants={itemVariants}>
              <ColumnHeading isRtl={isRtl}>{t('footer.treatments')}</ColumnHeading>
              <ul className="space-y-3">
                <FooterLink href={localizedPath('/treatments/dental-implants')} isRtl={isRtl}>
                  {t('treatments.dentalImplants')}
                </FooterLink>
                <FooterLink href={localizedPath('/treatments/teeth-whitening')} isRtl={isRtl}>
                  {t('treatments.teethWhitening')}
                </FooterLink>
                <FooterLink href={localizedPath('/treatments/dental-veneers')} isRtl={isRtl}>
                  {t('treatments.dentalVeneers')}
                </FooterLink>
                <FooterLink href={localizedPath('/treatments/hollywood-smile')} isRtl={isRtl}>
                  {t('treatments.hollywoodSmile')}
                </FooterLink>
                <FooterLink href={localizedPath('/treatments/all-on-4-implants')} isRtl={isRtl}>
                  {t('treatments.allOnFour')}
                </FooterLink>
                <FooterLink
                  href={localizedPath('/treatments/root-canal-treatment')}
                  isRtl={isRtl}
                >
                  {t('treatments.rootCanal')}
                </FooterLink>
              </ul>
            </motion.div>

            {/* Col 4: Contact */}
            <motion.div variants={itemVariants}>
              <ColumnHeading isRtl={isRtl}>{t('footer.contactInfo')}</ColumnHeading>
              <ul className="space-y-4">
                <ContactRow icon={<Phone className="w-4 h-4" />} isRtl={isRtl}>
                  <a
                    href={`tel:${businessData.phone}`}
                    className="hover:text-white transition-colors phone-number"
                  >
                    {businessData.phone}
                  </a>
                </ContactRow>
                <ContactRow icon={<Mail className="w-4 h-4" />} isRtl={isRtl}>
                  <a
                    href="mailto:info@dentawista.com"
                    className="hover:text-white transition-colors"
                  >
                    {t('footer.email')}
                  </a>
                </ContactRow>
                <ContactRow icon={<MapPin className="w-4 h-4" />} isRtl={isRtl}>
                  <a
                    href="https://maps.app.goo.gl/PVWvgLsQdpWUdQGE6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn('hover:text-white transition-colors', isRtl && 'font-arabic')}
                  >
                    {t('footer.address')}
                  </a>
                </ContactRow>
              </ul>
            </motion.div>

            {/* Col 5: Office Hours */}
            <motion.div variants={itemVariants}>
              <ColumnHeading isRtl={isRtl}>{t('footer.officeHours')}</ColumnHeading>

              {/* Currently open/closed badge */}
              {openLabel && (
                <div className="mb-5">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full',
                      isOpen
                        ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/25'
                        : 'bg-red-500/15 text-red-400 ring-1 ring-red-500/25'
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block w-1.5 h-1.5 rounded-full',
                        isOpen ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'
                      )}
                    />
                    {openLabel}
                  </span>
                </div>
              )}

              <ul className="space-y-2.5">
                <HoursRow day={t('footer.mondaySaturday')} hours="10:00 – 19:00" closedLabel={t('footer.closed')} />
                <HoursRow day={t('footer.sunday')} closed closedLabel={t('footer.closed')} />
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ── Band 2: Bottom bar ────────────────────────────────────────────────── */}
      <div className="bg-[#1a2242] border-t border-white/10">
        <div
          className={cn(
            'max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-white/70 text-xs',
            isRtl && 'font-arabic'
          )}
        >
          {/* Copyright — year pulled from runtime so the footer never goes stale. */}
          <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>

          {/* Legal links */}
          <div className="flex items-center gap-5">
            <Link
              href={localizedPath('/privacy')}
              className="text-white/80 hover:text-white transition-colors"
            >
              {t('footer.privacyPolicy')}
            </Link>
            <span className="text-white/40">|</span>
            <Link
              href={localizedPath('/terms')}
              className="text-white/80 hover:text-white transition-colors"
            >
              {t('footer.termsOfService')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
